import Factory from '@/Factories/Factory';
import * as interfaces from '#/CoreFrameInterfaces';
import RADApplication from '@/Management/RADApplication';
import ViewFactoryTuple from '@/Factories/ViewFactoryTuple';
import ViewModel from '@/ViewModels/ViewModel';
import View from '@/Views/View';
import AsyncTypeGuards from '@/Utility/AsyncTypeGuards';
import NavigationService from '@/Management/NavigationService';

/**
* Repräsentiert die Oberklasse aller ViewFactories. Erzeugt das View-Bundle und verkünpft dieses
*/
export default class ViewFactory extends Factory {

    /** 
    * Der Name der Factory
    */
    public name: string;

    /** 
     * Die ID der ViewFactory
     */
    public id: string;

    /** 
     * Die PDO-ID einer View
     */
    public viewId: string;

    /** 
     * Der Name der View
     */
    public viewName: string;

    /** 
     * Der Name der Factory
     */
    public factory: string;

    /** 
     * Ein ergonomischer Bezeichner
     */
    public ergName: string;

    /** 
     * Das verwendete ViewModel
     */
    public viewModel: ViewModel;

    /** 
     * Die verwendete View
     */
    public view: View;

    public tuple: ViewFactoryTuple;

    constructor(params: interfaces.IViewFactoryConstructorParameter) {
        super(params);

        this.name = this.paramOrDefault(params.name, null, this.escapeValue);
        this.id = this.paramOrDefault(_.uniqueId(this.name), null, this.escapeValue);
        this.viewId = this.paramOrDefault(params.ViewId || params.viewId, '', this.escapeValue);
        this.viewName = this.paramOrDefault(params.ViewName || params.viewName, '', this.escapeValue);
        this.factory = this.paramOrDefault(params.Factory, '', this.escapeValue);
        this.ergName = this.paramOrDefault(params.Title || params.ErgName || params.ergName, '', this.escapeValue);
        this.viewModel = this.paramOrDefault(params.viewModel, null);
        this.view = this.paramOrDefault(params.view, null);
        this.undisposeProperties.push('id', 'name');
    }

    /**
     * Erstellt eine ViewFactory anhand eines Namens und kümmert sich um die synchrone oder asynchrone Erstellung des Tupels.
     * @param name Der Name der zu erstellenden ViewFactory
     */
    public async createInstanceAsync(name: string) {
        const viewFactory = Factory.createInstance<ViewFactory>(name, { name });
        const tuple = AsyncTypeGuards.isAsyncTupleProvider<ViewFactoryTuple>(viewFactory)
            ? await viewFactory.getTupleAsync()
            : viewFactory.getTuple();
        return {
            viewFactory,
            tuple,
        };
    }

    /** 
    *  Ermittelt, ob abhängige Factories bereits erzeugt wurden und erzeugt diese, falls nicht vorhanden
    */
    public resolveDependencies(dependencies: Array<string>) {
        for (let i = 0; i < dependencies.length; i++) {
            const dependency = dependencies[i];
            const element = $('[data-view="' + dependency + '"]');

            if (element.length === 0) {
                const viewFactory = Factory.createInstance<ViewFactory>(dependency, { name: dependency });
                const tuple = viewFactory.getTuple();
                NavigationService.getInstance().addToRegion(viewFactory.getRegion(), tuple.view);
            }
        }
    }

    /**
    * Setzt den Seitentitel des Browsers
    */
    public setPageTitle(value: string): void {
        if (value) {
            value += ' - ';
        }

        value += RADApplication.getInstance().appName();

        document.title = value;
    }

    /** 
    * Erzeugt ein neues View-Element in einer Region
    */
    public createRootElement(view: string) {
        return $('<div data-view="' + view + '" data-factory="' + this.id + '">');
    }

    public getRegion(): string {
        return "Content";
    }

    /**
     * Gibt an, ob die Region vor dem Hinzufügen der View geleert werden soll.
     * Standardmäßig wird dies nur für die 'Modal'-Region nicht getan.
     */
    public shouldReplaceRegion(): boolean {
        return this.getRegion() !== 'Modal';
    }

    /** 
    * Prüft, ob eine Weiterleitung notwendig ist, und gibt diese zurück
    */
    public getNecessaryRedirect() : false | string {
        return ViewFactory.getNecessaryRedirectStatic();
    }

    public static getNecessaryRedirectStatic(): false | string {
        let isLoggedIn = RADApplication.getInstance().user && RADApplication.getInstance().user.isLoggedIn();

        if (isLoggedIn) {
            return false;
        }

        return 'Login';
    }

    /** 
    * IRefreshable-Implementierung bei Zurück-Navigation
    */
    public refresh() {
        return true;
    }

    /**
    * Disposed die ViewFactory
    */
    protected disposeInternal() {
        if (this.viewModel != null) {
            if (!this.viewModel.isDisposed) {
                this.viewModel.dispose();
            }
            this.viewModel = null;
        }

        if (this.view != null) {
            if (!this.view.isDisposed) {
                this.view.dispose();
            }
            this.view = null;
        }

        if (this.tuple != null) {
            if (!this.tuple.isDisposed) {
                this.tuple.dispose();
            }
            this.tuple = null;
        }

        super.disposeInternal();
    }

    /**
    * Erzeugt das FactoryTuple und die Factory
    */
    public getTuple() {
        if (this.tuple)
            return this.tuple;

        const $view = this.getView();
        this.tuple = new ViewFactoryTuple(this, $view);

        return this.tuple;
    }

    public disposeView() {
        if (!this.view)
            return;

        this.view.dispose();
        this.view = null;

        // Das Element im Tupel muss auch geleert werden, da auf ihm sonst keine Bindings mehr funktionieren
        this.tuple.view = null;

        // Das Tupel ist ohne Element nutzlos. Wir disposen es jedoch nicht, da wir sonst die Factory (uns) auch disposen würden.
        this.tuple = null;
    }

    public getView(): JQuery<HTMLElement> {
        throw new Error('Die Funktion "getView" wurde nicht implementiert');
    }

    /**
    * Escaped einen Wert
    */
    private escapeValue(value) {
        if (_.isObject(value)) {
            return value;
        }

        // Hier muss ggf. decodiert und NICHT encodiert werden, da dieser wert ggf. von der encodierten URL kommt!
        return decodeURIComponent(value);
    }
}